import { useMemo } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { AmbientBubbles } from '@/components/motion/AmbientBubbles';
import { TabStrip } from '@/components/motion/TabStrip';
import { OwnerSwitcher } from '@/components/OwnerSwitcher';
import { ThemeToggle } from '@/components/ThemeToggle';
import { Button } from '@/components/ui/button';
import { useMotionTier } from '@/hooks/useMotionTier';
import { useLiveStatus } from '@/lib/live-status';
import { useNavActiveId } from '@/lib/motion';
import { cn } from '@/lib/utils';

function HeaderDivider({ className }: { className?: string }) {
  return <span aria-hidden className={cn('h-5 w-px shrink-0 bg-[var(--color-line)]', className)} />;
}

const TIER_LABEL = { subtle: 'FX: Low', normal: 'FX: Med', arcade: 'FX: Max' } as const;

export function AppHeader() {
  const { pathname } = useLocation();
  const activeId = useNavActiveId(pathname);
  const { t } = useTranslation();
  const { connected } = useLiveStatus();
  const { tier, cycleTier, reducedMotion } = useMotionTier();
  const onLiveRoute = pathname === '/';

  const navTabs = useMemo(
    () => [
      { id: '/', to: '/', label: t('nav.liveSessions'), end: true },
      { id: '/history', to: '/history', label: t('nav.matchHistory'), end: false },
      { id: '/sessions/new', to: '/sessions/new', label: t('nav.newSession'), end: false },
    ],
    [t],
  );

  return (
    <header className="oche-surface relative sticky top-0 z-30 overflow-visible border-b border-[var(--color-line)] bg-[var(--color-surface)]/90 backdrop-blur-md">
      {onLiveRoute && connected && tier === 'arcade' ? (
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <AmbientBubbles active density={8} />
        </div>
      ) : null}

      <div className="relative mx-auto max-w-5xl px-4 sm:px-5">
        <div className="flex h-14 items-center gap-3 sm:gap-4">
          <span className="score shrink-0 text-lg tracking-tight oche-line pb-0.5 sm:text-xl">OCHE</span>

          <nav
            className="min-w-0 flex-1 overflow-x-auto [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
            aria-label="Primary"
          >
            <div className="inline-flex shrink-0 rounded-lg border border-[var(--color-line)] bg-[var(--color-canvas)]/70 p-0.5">
              <TabStrip items={navTabs} activeId={activeId} ariaLabel="Primary" />
            </div>
          </nav>

          <div className="flex shrink-0 items-center gap-2 overflow-visible sm:gap-2.5">
            <OwnerSwitcher compact />

            <HeaderDivider className="hidden sm:block" />

            <div className="flex items-center gap-0.5">
              {!reducedMotion ? (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="hidden h-9 px-2 text-[10px] uppercase tracking-wide sm:inline-flex"
                  onClick={cycleTier}
                  title="Cycle motion intensity"
                >
                  {TIER_LABEL[tier]}
                </Button>
              ) : null}
              <Button variant="ghost" size="sm" className="hidden h-9 px-2.5 text-xs md:inline-flex" asChild>
                <Link to="/settings/keys">{t('nav.apiKeys')}</Link>
              </Button>
              <ThemeToggle />
            </div>
          </div>
        </div>
      </div>
    </header>
  );
}
